import { useState } from 'react';
import styles from '../../MetaReportViewer.module.css';
import { MONETARY_FIELD_KEYS, fmt, fmtMoney, resolveCurrency } from './commonData';

const PerfField = ({ label, value, fieldKey, currency }) => {
  const isEmpty = value == null || value === '';
  const displayValue = MONETARY_FIELD_KEYS.has(fieldKey)
    ? fmtMoney(value, currency)
    : Array.isArray(value)
      ? value.join(', ')
      : typeof value === 'object'
        ? JSON.stringify(value)
        : String(value);

  return (
    <div className={styles.perfField}>
      <span className={styles.perfFieldLabel}>{label}</span>
      {isEmpty
        ? <span className={styles.perfFieldValueNull}>—</span>
        : <span className={styles.perfFieldValue}>{displayValue}</span>
      }
    </div>
  );
};

const InlineAdPreview = ({ row }) => {
  const hasPreview =
    Boolean(row.primary_text) ||
    Boolean(row.headline) ||
    Boolean(row.ad_thumbnail_url) ||
    Boolean(row.call_to_action) ||
    Boolean(row.destination_url);

  if (!hasPreview) return null;

  return (
    <div className={styles.adEmbedSection}>
      <span className={styles.perfGroupLabel}>Sample Ad Preview</span>
      <div className={styles.sampleAdCard}>
        {row.primary_text && (
          <div className={styles.sampleAdCopy}>
            <p className={styles.sampleAdPrimaryText}>{row.primary_text}</p>
          </div>
        )}
        {/* {row.ad_thumbnail_url && (
          <a
            href={row.ad_permalink_url || row.ad_thumbnail_url}
            target="_blank"
            rel="noopener noreferrer"
            className={styles.adThumbnailLink}
          >
            <img
              src={row.ad_thumbnail_url}
              alt={row.headline || row.ad_name || 'Ad creative'}
              className={styles.adThumbnail}
            />
          </a>
        )} */}
        {(row.headline || row.call_to_action || row.destination_url) && (
          <div className={styles.sampleAdFooter}>
            <div className={styles.sampleAdMeta}>
              {row.destination_url && (
                <a
                  href={row.destination_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.sampleAdDomain}
                >
                  {row.destination_url}
                </a>
              )}
              {row.headline && (
                <p className={styles.sampleAdHeadline}>{row.headline}</p>
              )}
            </div>
            {row.call_to_action && (
              <span className={styles.sampleAdCta}>{row.call_to_action.replace(/_/g, ' ')}</span>
            )}
          </div>
        )}
      </div>
      {row.ad_permalink_url && (
        <a
          href={row.ad_permalink_url}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.adViewLink}
        >
          Open on Facebook ↗
        </a>
      )}
    </div>
  );
};

const AttributionBlock = ({ row, currency = null }) => {
  const hasAttribution = row.purchasing_customers > 0 || row.aov != null;
  if (!hasAttribution) return null;
  const rowCurrency = resolveCurrency(row, currency);
  const tiles = [
    { label: 'Customers', value: fmt(row.purchasing_customers, 0) },
    { label: 'AOV', value: fmtMoney(row.aov, rowCurrency) },
    { label: 'CAC', value: fmtMoney(row.cac, rowCurrency) },
    { label: 'Repeat', value: fmt(row.repeat_customers, 0) },
    { label: 'Repeat Rate', value: row.repeat_rate != null ? `${fmt(row.repeat_rate)}%` : null },
    { label: '90-Day CLTV', value: fmtMoney(row['90_day_cltv'], rowCurrency) },
    { label: 'CLTV:CAC', value: fmt(row.cltv_cac) },
  ].filter((tile) => tile.value && tile.value !== '—');

  return (
    <div className={styles.attrBlock}>
      <span className={styles.attrTitle}>Shopify Attribution</span>
      <div className={styles.chipRow}>
        {tiles.map((tile) => (
          <div key={tile.label} className={`${styles.chip} ${styles.chipAttr}`}>
            <span className={styles.chipLabel}>{tile.label}</span>
            <span className={styles.chipValue}>{tile.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const VideoBlock = ({ row }) => {
  const hasVideo = row.video_views > 0 || row.video_thruplays > 0;
  if (!hasVideo) return null;
  const chips = [
    { label: 'Video Views (3s)', value: fmt(row.video_views, 0) },
    { label: 'ThruPlays', value: fmt(row.video_thruplays, 0) },
    { label: 'Video 25%', value: fmt(row.video_p25, 0) },
    { label: 'Hook Rate', value: row.hook_rate != null ? `${fmt(row.hook_rate)}%` : null },
    { label: 'Hold Rate', value: row.video_hold_rate != null ? `${fmt(row.video_hold_rate)}%` : null },
  ].filter((chip) => chip.value && chip.value !== '—');

  return (
    <div className={styles.videoBlock}>
      <span className={styles.funnelTitle}>Video</span>
      <div className={styles.chipRow}>
        {chips.map((chip) => (
          <div key={chip.label} className={styles.chip}>
            <span className={styles.chipLabel}>{chip.label}</span>
            <span className={styles.chipValue}>{chip.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

const CollapsibleSection = ({ label, fields, row, defaultOpen = false, currency = null }) => {
  const [open, setOpen] = useState(defaultOpen);
  const visible = fields.filter((field) => row[field.key] != null && row[field.key] !== '');
  const rowCurrency = resolveCurrency(row, currency);
  if (!visible.length) return null;
  return (
    <div className={styles.collapsible}>
      <button className={styles.collapsibleHeader} onClick={() => setOpen((value) => !value)}>
        <span className={styles.collapsibleLabel}>{label}</span>
        <span className={styles.collapsibleChevron}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div className={styles.perfFields}>
          {visible.map((field) => (
            <PerfField
              key={field.key}
              label={field.label}
              value={row[field.key]}
              fieldKey={field.key}
              currency={rowCurrency}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export {
  AttributionBlock,
  CollapsibleSection,
  InlineAdPreview,
  PerfField,
  VideoBlock,
};
